import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useKeyPressEvent, useClickAway } from 'react-use';
import { useTheme } from 'styled-components';

import { ModalProps } from './Modal.types';
import { CloseButton, StyledModal, Backdrop, getModalStyles } from './Modal.styles';
import { XIcon } from 'src/packages/icons';
import { DarkTheme, LightTheme } from 'src/packages/theme';

const Modal: React.FC<ModalProps> = (props: ModalProps) => {
  const ref = React.useRef(null);

  const {
    isOpen = false,
    hideCloseButton = false,
    mountNode = document.body,
    size = 'medium',
    width,
    height,
    preventOnClickOutside = false,
    onClose = () => {},
    backgroundColor,
    backdropBlur = false,
    backdropBgColor,
    theme,
    children,
    ...rest
  } = props;

  const contextTheme = useTheme();

  const defaultTheme = useMemo(() => {
    return theme
      ? theme === 'light'
        ? { ...LightTheme, rtl: contextTheme.rtl }
        : { ...DarkTheme, rtl: contextTheme.rtl }
      : contextTheme;
  }, [contextTheme, theme]);

  const modalStyles = useMemo(() => {
    return getModalStyles(size, width, height);
  }, [size, width]);

  const disableBodyScroll = () => {
    const html = document.querySelector('html');
    html && (html.style.overflow = 'hidden');
  };

  const enableBodyScroll = () => {
    const html = document.querySelector('html');
    html && (html.style.overflow = 'unset');
  };

  useEffect(() => {
    return () => {
      enableBodyScroll();
    };
  }, []);

  useEffect(() => {
    if (ref?.current) {
      if (isOpen) {
        disableBodyScroll();
      } else {
        enableBodyScroll();
      }
    } else {
      enableBodyScroll();
    }
  }, [isOpen, ref?.current]);

  useKeyPressEvent('Escape', onClose);
  useClickAway(ref, preventOnClickOutside ? () => {} : onClose);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <Backdrop backdropBlur={backdropBlur} backdropBgColor={backdropBgColor}>
      <StyledModal
        defaulttheme={defaultTheme}
        modalStyles={modalStyles}
        {...rest}
        size={size}
        backgroundColor={backgroundColor}
        ref={ref}
      >
        {!hideCloseButton && (
          <CloseButton onClick={onClose}>
            <XIcon size={20} color={defaultTheme.colors.text.tertiary} />
          </CloseButton>
        )}

        {children}
      </StyledModal>
    </Backdrop>,
    mountNode
  );
};

export default Modal;
